<template>
  <div class="device-offline-chart-panel">
    <div class="device-offline-chart-panel__header">
      <h2 class="general-indicators__section-title mb-0">Устройства «Не в сети»</h2>
      <div class="device-offline-chart-panel__actions">
        <v-btn-toggle
          v-model="selectedPeriod"
          mandatory
          dense
          rounded
          color="primary"
          class="device-offline-chart-panel__period"
          @change="onPeriodChange"
        >
          <v-btn
            v-for="option in periodOptions"
            :key="option.value"
            :value="option.value"
            small
            class="text-none"
          >
            {{ option.label }}
          </v-btn>
        </v-btn-toggle>

        <v-menu
          v-if="isDayMode"
          v-model="dayMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="selectedDay"
              class="device-offline-chart-panel__day-field"
              dense
              outlined
              hide-details
              readonly
              prepend-inner-icon="mdi-calendar"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="selectedDay"
            :max="maxDate"
            @input="onDayChange"
          />
        </v-menu>

        <v-btn
          color="primary"
          outlined
          rounded
          small
          class="text-none device-offline-chart-panel__refresh"
          :loading="loading"
          :disabled="loading"
          @click="onRefresh"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Обновить
        </v-btn>
      </div>
    </div>

    <p class="device-offline-chart-panel__hint">
      Ежедневная статистика установленных устройств со статусом «Не в сети».
      <span v-if="isSingleDayChart && items.length" class="device-offline-chart-panel__hint-date">
        Дата: {{ formatChartDate(items[0].date) }}
      </span>
    </p>

    <div class="device-offline-chart-panel__series">
      <v-chip
        v-for="series in seriesOptions"
        :key="series.key"
        class="mr-2 mb-2 device-offline-chart-panel__series-chip"
        :class="{ 'device-offline-chart-panel__series-chip--off': !visibleSeries[series.key] }"
        :color="series.color"
        :outlined="!visibleSeries[series.key]"
        :text-color="visibleSeries[series.key] ? 'white' : series.color"
        label
        small
        @click="toggleSeries(series.key)"
      >
        <v-icon left x-small>
          {{ visibleSeries[series.key] ? 'mdi-eye' : 'mdi-eye-off' }}
        </v-icon>
        <span class="device-offline-chart-panel__series-label">{{ series.label }}</span>
      </v-chip>
    </div>

    <div v-if="hasChartData && hasVisibleSeries" class="device-offline-chart-panel__totals">
      <span class="device-offline-chart-panel__totals-main">
        Всего: <strong>{{ totals.total }}</strong>
      </span>
      <span
        v-for="series in visibleSeriesOptions"
        :key="'total-' + series.key"
        class="device-offline-chart-panel__totals-item"
        :class="{ 'device-offline-chart-panel__totals-item--clickable': totals[series.key] > 0 }"
        @click="openDetailsForLatest(series.key)"
      >
        {{ series.label }}: <strong>{{ totals[series.key] }}</strong>
      </span>
      <span v-if="!isSingleDayChart" class="device-offline-chart-panel__totals-note">
        на {{ formatChartDate(latestItem.date) }}
      </span>
      <span class="device-offline-chart-panel__totals-hint">
        Нажмите на ненулевое значение, чтобы открыть список устройств
      </span>
    </div>

    <v-progress-linear
      v-if="devicesDetailsLoading"
      indeterminate
      color="primary"
      class="mb-2"
    />

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <v-card v-else flat outlined class="device-offline-chart-panel__chart-card pa-4">
      <div v-if="hasChartData && hasVisibleSeries" class="device-offline-chart-panel__canvas-wrap">
        <canvas ref="offlineChart"></canvas>
      </div>
      <div v-else class="device-offline-chart-panel__empty">
        {{ emptyMessage }}
      </div>
    </v-card>

    <device-offline-daily-devices-dialog
      v-model="devicesDialog"
      :loading="devicesLoading"
      :date="devicesDate"
      :type="devicesType"
      :items="devicesItems"
      :is-live="devicesIsLive"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Chart from 'chart.js';
import ruDateFormatMixin from '@/mixins/ruDateFormat';
import DeviceOfflineDailyDevicesDialog from '@/components/reports/DeviceOfflineDailyDevicesDialog.vue';

const SERIES_OPTIONS = [
  {
    key: 'doorphones',
    label: 'Домофоны',
    field: 'doorphones_offline_count',
    color: 'rgba(25, 118, 210, 1)',
    background: 'rgba(25, 118, 210, 0.08)',
  },
  {
    key: 'barriers',
    label: 'Шлагбаумы',
    field: 'barriers_offline_count',
    color: 'rgba(255, 152, 0, 1)',
    background: 'rgba(255, 152, 0, 0.08)',
  },
  {
    key: 'gates',
    label: 'Калитки',
    field: 'gates_offline_count',
    color: 'rgba(76, 175, 80, 1)',
    background: 'rgba(76, 175, 80, 0.08)',
  },
];

const pointValueLabelsPlugin = {
  afterDatasetsDraw (chart) {
    const { ctx } = chart;
    if (!ctx) return;

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!meta || meta.hidden) return;

      meta.data.forEach((element, index) => {
        const raw = dataset.data[index];
        if (raw == null || raw === '') return;

        const model = element._model || {};
        const x = model.x;
        const y = model.y;
        if (x == null || y == null) return;

        ctx.save();
        ctx.font = 'bold 11px Montserrat, Arial, sans-serif';
        ctx.fillStyle = dataset.borderColor || dataset.backgroundColor || '#37474f';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.fillText(String(raw), x, y - 6);
        ctx.restore();
      });
    });
  },
};

function todayIsoDate () {
  return new Date().toISOString().slice(0, 10);
}

function yesterdayIsoDate () {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return date.toISOString().slice(0, 10);
}

export default {
  name: 'DeviceOfflineDailyChartPanel',
  components: {
    DeviceOfflineDailyDevicesDialog,
  },
  mixins: [ruDateFormatMixin],
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    filter: {
      type: Object,
      default: () => ({ type: 'preset', days: 30 }),
    },
  },
  data () {
    return {
      selectedPeriod: this.resolveSelectedPeriod(this.filter),
      selectedDay: this.filter?.date || yesterdayIsoDate(),
      dayMenu: false,
      offlineChart: null,
      periodOptions: [
        { value: 'day', label: 'День' },
        { value: '7', label: '7 дней' },
        { value: '30', label: '30 дней' },
        { value: '90', label: '90 дней' },
        { value: '180', label: '180 дней' },
      ],
      seriesOptions: SERIES_OPTIONS,
      visibleSeries: {
        doorphones: true,
        barriers: true,
        gates: true,
      },
      devicesDialog: false,
      devicesDetailsLoading: false,
      devicesLoading: false,
      devicesDate: '',
      devicesType: '',
      devicesItems: [],
      devicesIsLive: false,
    };
  },
  computed: {
    hasChartData () {
      return Array.isArray(this.items) && this.items.length > 0;
    },
    hasVisibleSeries () {
      return this.visibleSeriesOptions.length > 0;
    },
    visibleSeriesOptions () {
      return this.seriesOptions.filter((series) => this.visibleSeries[series.key]);
    },
    isDayMode () {
      return this.selectedPeriod === 'day';
    },
    isSingleDayChart () {
      return this.items.length === 1;
    },
    maxDate () {
      return todayIsoDate();
    },
    latestItem () {
      if (!this.items.length) return null;
      return this.items[this.items.length - 1];
    },
    totals () {
      const item = this.latestItem || {};
      const result = {
        doorphones: 0,
        barriers: 0,
        gates: 0,
        total: 0,
      };

      this.visibleSeriesOptions.forEach((series) => {
        const value = Number(item[series.field]) || 0;
        result[series.key] = value;
        result.total += value;
      });

      return result;
    },
    emptyMessage () {
      if (!this.hasChartData) {
        return 'Нет данных за выбранный период';
      }
      if (!this.hasVisibleSeries) {
        return 'Включите хотя бы один тип устройств на графике';
      }
      return 'Нет данных за выбранный период';
    },
  },
  watch: {
    items: {
      deep: true,
      handler () {
        this.$nextTick(() => this.renderChart());
      },
    },
    loading (value) {
      if (!value) {
        this.$nextTick(() => this.renderChart());
      }
    },
    hasChartData (value) {
      if (value) {
        this.$nextTick(() => this.renderChart());
      } else {
        this.destroyChart();
      }
    },
    filter: {
      deep: true,
      handler (value) {
        this.selectedPeriod = this.resolveSelectedPeriod(value);
        if (value?.type === 'day' && value.date) {
          this.selectedDay = value.date;
        }
        this.$nextTick(() => this.renderChart());
      },
    },
    selectedPeriod () {
      this.$nextTick(() => this.renderChart());
    },
    visibleSeries: {
      deep: true,
      handler () {
        this.$nextTick(() => this.renderChart());
      },
    },
  },
  mounted () {
    this.renderChart();
  },
  beforeDestroy () {
    this.destroyChart();
  },
  methods: {
    ...mapActions({
      getDeviceOfflineDailyStatisticsDevices: 'Reports/getDeviceOfflineDailyStatisticsDevices',
    }),
    resolveSelectedPeriod (filter) {
      if (filter?.type === 'day') return 'day';
      return String(filter?.days || 30);
    },
    buildPeriodPayload () {
      if (this.selectedPeriod === 'day') {
        return { type: 'day', date: this.selectedDay };
      }

      return { type: 'preset', days: Number(this.selectedPeriod) };
    },
    onPeriodChange (value) {
      if (!value) return;
      if (value === 'day' && !this.filter?.date) {
        this.selectedDay = yesterdayIsoDate();
      }
      this.$emit('change-period', this.buildPeriodPayload());
    },
    onDayChange (value) {
      if (!value) return;
      this.selectedDay = value;
      this.dayMenu = false;
      this.$emit('change-period', { type: 'day', date: value });
    },
    onRefresh () {
      this.$emit('refresh');
    },
    toggleSeries (key) {
      this.$set(this.visibleSeries, key, !this.visibleSeries[key]);
    },
    formatChartDate (dateString) {
      return this.ruDateFormat(dateString, true);
    },
    buildLineChartData () {
      const labels = this.items.map((item) => this.formatChartDate(item.date));

      return {
        labels,
        datasets: this.visibleSeriesOptions.map((series) => ({
          label: series.label,
          data: this.items.map((item) => item[series.field]),
          borderColor: series.color,
          backgroundColor: series.background,
          borderWidth: 2,
          fill: false,
          lineTension: 0.2,
          pointRadius: 4,
          pointHoverRadius: 6,
        })),
      };
    },
    buildSingleDayChartData () {
      const item = this.items[0];

      return {
        labels: this.visibleSeriesOptions.map((series) => series.label),
        datasets: [{
          label: this.formatChartDate(item.date),
          data: this.visibleSeriesOptions.map((series) => item[series.field]),
          backgroundColor: this.visibleSeriesOptions.map((series) => series.color),
          borderWidth: 0,
          maxBarThickness: 72,
        }],
      };
    },
    buildChartOptions () {
      const self = this;
      const common = {
        maintainAspectRatio: false,
        responsive: true,
        layout: {
          padding: {
            top: 18,
            right: 8,
          },
        },
        onClick (_event, elements) {
          if (!elements.length) return;
          const element = elements[0];
          self.handleChartClick(element._datasetIndex, element._index);
        },
        onHover (event, elements) {
          const target = event?.native?.target || event?.target;
          if (!target) return;

          if (!elements.length) {
            target.style.cursor = 'default';
            return;
          }

          const element = elements[0];
          target.style.cursor = self.isChartPointClickable(element._datasetIndex, element._index)
            ? 'pointer'
            : 'default';
        },
      };

      if (this.isSingleDayChart) {
        return {
          ...common,
          legend: {
            display: false,
          },
          tooltips: {
            mode: 'nearest',
            intersect: true,
            position: 'nearest',
            caretPadding: 8,
            callbacks: {
              title (tooltipItems, data) {
                return data.datasets[0]?.label || '';
              },
              label (tooltipItem, data) {
                const category = data.labels[tooltipItem.index] || '';
                return `${category}: ${tooltipItem.yLabel}`;
              },
            },
          },
          scales: {
            xAxes: [{
              gridLines: {
                display: false,
              },
            }],
            yAxes: [{
              ticks: {
                beginAtZero: true,
                precision: 0,
              },
            }],
          },
        };
      }

      return {
        ...common,
        legend: {
          display: false,
        },
        tooltips: {
          mode: 'nearest',
          intersect: false,
          position: 'nearest',
          caretPadding: 8,
          callbacks: {
            title (tooltipItems) {
              if (!tooltipItems.length) return '';
              return tooltipItems[0].xLabel || '';
            },
            label (tooltipItem, data) {
              const dataset = data.datasets[tooltipItem.datasetIndex] || {};
              return `${dataset.label || ''}: ${tooltipItem.yLabel}`;
            },
          },
        },
        scales: {
          xAxes: [{
            gridLines: {
              display: false,
            },
          }],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              precision: 0,
            },
          }],
        },
      };
    },
    renderChart () {
      if (!this.$refs.offlineChart || !this.hasChartData || !this.hasVisibleSeries) {
        this.destroyChart();
        return;
      }

      const chartType = this.isSingleDayChart ? 'bar' : 'line';
      const chartData = this.isSingleDayChart
        ? this.buildSingleDayChartData()
        : this.buildLineChartData();

      this.destroyChart();

      this.offlineChart = new Chart(this.$refs.offlineChart, {
        type: chartType,
        data: chartData,
        options: this.buildChartOptions(),
        plugins: [pointValueLabelsPlugin],
      });
    },
    destroyChart () {
      if (!this.offlineChart) return;
      this.offlineChart.destroy();
      this.offlineChart = null;
    },
    openDetailsForLatest (type) {
      if (!this.latestItem || !type) return;
      const series = this.seriesOptions.find((item) => item.key === type);
      if (!series) return;
      const value = Number(this.latestItem[series.field]) || 0;
      if (!value) return;
      this.openDeviceDetails(this.latestItem.date, type, value);
    },
    isChartPointClickable (datasetIndex, index) {
      if (this.isSingleDayChart) {
        const series = this.visibleSeriesOptions[index];
        const item = this.items[0];
        if (!series || !item) return false;
        return (Number(item[series.field]) || 0) > 0;
      }

      const series = this.visibleSeriesOptions[datasetIndex];
      const item = this.items[index];
      if (!series || !item) return false;
      return (Number(item[series.field]) || 0) > 0;
    },
    handleChartClick (datasetIndex, index) {
      if (!this.isChartPointClickable(datasetIndex, index)) return;

      if (this.isSingleDayChart) {
        const series = this.visibleSeriesOptions[index];
        const item = this.items[0];
        this.openDeviceDetails(item.date, series.key, Number(item[series.field]) || 0);
        return;
      }

      const series = this.visibleSeriesOptions[datasetIndex];
      const item = this.items[index];
      this.openDeviceDetails(item.date, series.key, Number(item[series.field]) || 0);
    },
    openDeviceDetails (date, type, expectedCount = 0) {
      if (!date || !type) return;
      if (!expectedCount) return;
      if (this.devicesDetailsLoading) return;

      this.devicesDetailsLoading = true;

      this.getDeviceOfflineDailyStatisticsDevices({ date, type })
        .then((response) => {
          const payload = response?.data || response;
          const items = payload?.items || [];
          const snapshotAvailable = payload?.snapshot_available !== false;

          if (!items.length) {
            this.$flash_warning(
              snapshotAvailable
                ? 'Нет устройств для отображения'
                : (payload?.message || 'Детализация за эту дату недоступна'),
            );
            return;
          }

          this.devicesDate = date;
          this.devicesType = type;
          this.devicesItems = items;
          this.devicesIsLive = !!payload?.is_live;
          this.devicesLoading = false;
          this.devicesDialog = true;
        })
        .catch(() => {
          this.$flash_warning('Ошибка при загрузке списка устройств «Не в сети»');
        })
        .finally(() => {
          this.devicesDetailsLoading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.device-offline-chart-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 8px;
}

.device-offline-chart-panel__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.device-offline-chart-panel__refresh {
  font-weight: 500;
}

.device-offline-chart-panel__day-field {
  max-width: 170px;
}

.device-offline-chart-panel__day-field ::v-deep .v-input__slot {
  min-height: 36px !important;
}

.device-offline-chart-panel__hint {
  margin: 0 0 12px;
  font-size: 0.875rem;
  color: #78909c;
}

.device-offline-chart-panel__hint-date {
  display: inline-block;
  margin-left: 8px;
  font-weight: 500;
  color: #1976d2;
}

.device-offline-chart-panel__series {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 8px;
}

.device-offline-chart-panel__series-chip {
  cursor: pointer;
  user-select: none;
}

.device-offline-chart-panel__series-chip--off {
  opacity: 0.72;
}

.device-offline-chart-panel__series-chip--off .device-offline-chart-panel__series-label {
  text-decoration: line-through;
}

.device-offline-chart-panel__totals {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px 16px;
  margin: 0 0 14px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f5f9ff;
  border: 1px solid #e3eaf3;
  font-size: 0.875rem;
  color: #455a64;
}

.device-offline-chart-panel__totals-main {
  color: #0d4092;
  font-size: 0.95rem;
}

.device-offline-chart-panel__totals-item strong,
.device-offline-chart-panel__totals-main strong {
  font-weight: 700;
}

.device-offline-chart-panel__totals-item--clickable {
  cursor: pointer;
  border-radius: 6px;
  padding: 2px 6px;
  margin: -2px -6px;
  transition: background-color 0.15s ease;
}

.device-offline-chart-panel__totals-item--clickable:hover {
  background: rgba(25, 118, 210, 0.08);
}

.device-offline-chart-panel__totals-hint {
  flex-basis: 100%;
  color: #90a4ae;
  font-size: 0.76rem;
}

.device-offline-chart-panel__totals-note {
  color: #90a4ae;
  font-size: 0.78rem;
}

.device-offline-chart-panel__chart-card {
  border-radius: 14px;
}

.device-offline-chart-panel__canvas-wrap {
  position: relative;
  height: 360px;
}

.device-offline-chart-panel__empty {
  height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  color: #90a4ae;
  text-align: center;
  padding: 0 16px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 10px;
}

.device-offline-chart-panel__period {
  border: 1px solid rgba(25, 118, 210, 0.35);
  border-radius: 24px;
  overflow: hidden;
  background: #fff;
  flex-wrap: wrap;
}

.device-offline-chart-panel__period ::v-deep .v-btn {
  text-transform: none;
  font-weight: 500;
  min-width: 72px;
  border-radius: 0 !important;
  letter-spacing: normal;
}

.device-offline-chart-panel__period ::v-deep .v-btn:not(.v-btn--active) {
  color: #1976d2;
  background-color: #fff !important;
}

.device-offline-chart-panel__period ::v-deep .v-btn.v-btn--active {
  color: #fff !important;
  background-color: #1976d2 !important;
  box-shadow: inset 0 0 0 1px #1976d2;
}
</style>
